import React, { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import PlanSponsorSummaryView from './PlanSponsorSummary-view';
import { DataChart } from '../../../../components';

// Extend dayjs with isBetween plugin
dayjs.extend(isBetween);

const PlanSponsorSummary = ({
  participantInvestments,
  participantAgeRanges,
  planSponsorSummaryData,
}) => {
  const [chartTimeFrame, setChartTimeFrame] = useState('ALL');
  const [customDates, setCustomDates] = useState({
    startDate: dayjs('0001-01-01'),
    endDate: dayjs(),
  });
  const [showFields, setShowFields] = useState(false);
  const [horizontalBarChart, setHorizontalBarChart] = useState(null);

  const timeFrames = [
    {
      label: 'Custom',
      date: { startDate: customDates.startDate, endDate: customDates.endDate },
    },
    {
      label: '1M',
      date: dayjs().add(-1, 'M'),
    },
    {
      label: '3M',
      date: dayjs().add(-3, 'M'),
    },
    {
      label: '6M',
      date: dayjs().add(-6, 'M'),
    },
    {
      label: 'YTD',
      date: dayjs().startOf('year'),
    },
    {
      label: '1Y',
      date: dayjs().add(-1, 'y'),
    },
    {
      label: 'ALL',
      date: dayjs('0001-01-01'),
    },
  ];

  const value = useMemo(() => {
    if (
      (planSponsorSummaryData?.gapAnalysis.length ?? 0) === 0 &&
      (planSponsorSummaryData?.topInteractions.length ?? 0) === 0
    ) {
      return {
        shoppingCart: [],
        topInteraction: [],
        timeFrames: [],
      };
    }
    const shoppingCartHist = planSponsorSummaryData?.gapAnalysis.map((x) => ({
      date: dayjs(x.shoppingCartDate ?? new Date()).format(),
      investmentStrategyId: x.investmentStrategyId,
      participantId: x.participantId,
    }));
    const topInteractionHist = planSponsorSummaryData?.topInteractions.map(
      (x) => ({
        date: dayjs(x.customerInteractionDate).format(),
        interactionMethodDescription: x.interactionMethodDescription,
      })
    );

    return {
      shoppingCart: shoppingCartHist,
      topInteraction: topInteractionHist,
    };
  }, [
    planSponsorSummaryData?.gapAnalysis,
    planSponsorSummaryData?.topInteractions,
  ]);

  const filtered = useMemo(() => {
    const map = {
      '1M': dayjs().add(-1, 'M'),
      '3M': dayjs().add(-3, 'M'),
      '6M': dayjs().add(-6, 'M'),
      YTD: dayjs().startOf('year'),
      '1Y': dayjs().add(-1, 'y'),
      ALL: dayjs('0001-01-01'),
    };

    const defaultShoppingCartInFrame = value?.shoppingCart?.filter((x) =>
      dayjs(x.date).isAfter(map[chartTimeFrame])
    );

    const customShoppingCartInFrame = value?.shoppingCart?.filter((x) =>
      dayjs(x.date).isBetween(customDates.startDate, customDates.endDate)
    );

    const shoppingCartInFrame =
      chartTimeFrame !== 'Custom'
        ? defaultShoppingCartInFrame
        : customShoppingCartInFrame;

    const uniqueShoppingCartInFrame = shoppingCartInFrame?.filter(
      (x, i) =>
        shoppingCartInFrame.findIndex(
          (s) => x.participantId === s.participantId
        ) === i
    );
    const defaultTopInteractionInFrame = value?.topInteraction?.filter((x) =>
      dayjs(x.date).isAfter(map[chartTimeFrame])
    );

    const customTopInteractionInFrame = value?.topInteraction?.filter((x) =>
      dayjs(x.date).isBetween(customDates.startDate, customDates.endDate)
    );

    const topInteractionInFrame =
      chartTimeFrame !== 'Custom'
        ? defaultTopInteractionInFrame
        : customTopInteractionInFrame;

    const topInteractionLabel = topInteractionInFrame?.reduce((accu, curr) => {
      var interactionMethod = curr.interactionMethodDescription;
      if (!accu.hasOwnProperty(interactionMethod)) {
        accu[interactionMethod] = 0;
      }
      accu[interactionMethod]++;
      return accu;
    }, {});
    let participantInteraction = [];
    if (topInteractionLabel) {
      participantInteraction = Object.keys(topInteractionLabel).map((k) => ({
        label: k,
        value: topInteractionLabel[k],
      }));
    }
    participantInteraction.sort((a, b) => (a.value < b.value ? 1 : -1));
    return {
      startedGapAnalysis: uniqueShoppingCartInFrame?.length,
      completedGapAnalysis: uniqueShoppingCartInFrame?.filter(
        (x) => x.investmentStrategyId !== null
      ).length,
      topInteractions: participantInteraction,
      totalCustomerInteractions: topInteractionInFrame?.length,
    };
  }, [chartTimeFrame, value?.shoppingCart, value?.topInteraction]);

  const updateCustomTimeFrame = (label, start, end) => {
    let newCustomStartDate = dayjs(start);
    let newCustomEndDate = dayjs(end);

    if (label !== chartTimeFrame) setHorizontalBarChart(null);
    setShowFields(!showFields);
    setCustomDates({
      startDate: newCustomStartDate,
      endDate: newCustomEndDate,
    });
    setChartTimeFrame(label);
  };

  const updateTimeFrame = (label) => {
    if (label === 'Custom') {
      setShowFields(!showFields);
    } else {
      if (label !== 'Custom') setShowFields(false);
      if (label !== chartTimeFrame) setHorizontalBarChart(null);
      setChartTimeFrame(label);
    }
  };

  useEffect(() => {
    const horizontalBar = (
      <DataChart
        loading={planSponsorSummaryData?.topInteractions == null}
        data={filtered.topInteractions ?? []}
        type="bar"
        direction="horizontal"
        dataLength={filtered.topInteractions.length ?? 0}
        height={60}
      />
    );
    setHorizontalBarChart(horizontalBar);
  }, [
    filtered.topInteractions,
    planSponsorSummaryData?.topInteractions,
    chartTimeFrame,
  ]);

  return (
    <PlanSponsorSummaryView
      timeFrames={timeFrames}
      updateTimeFrame={updateTimeFrame}
      chartTimeFrame={chartTimeFrame}
      planSponsorSummaryData={planSponsorSummaryData ?? null}
      participantInvestments={participantInvestments}
      participantAgeRanges={participantAgeRanges}
      filtered={filtered}
      horizontalBarChart={horizontalBarChart}
      showFields={showFields}
      updateCustomTimeFrame={updateCustomTimeFrame}
    />
  );
};

export default PlanSponsorSummary;
